/**
 * CallsTable Component
 * 
 * Modern dark-themed call logs table matching Figma design.
 * Features search, filters, pagination, and detailed call information.
 * 
 * @example
 * <CallsTable 
 *   calls={calls}
 *   isLoading={false}
 *   onRowClick={(call) => navigate(`/calls/${call.call_id}`)}
 * />
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Play, Pause, Search, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import CallDetailModal from './CallDetailModal';
import { formatDate, formatDurationMMSS, formatPhoneNumber } from '../../utils/formatters';

/**
 * CallsTable component
 * 
 * @param {object} props - Component props
 * @param {Array} props.calls - Array of call objects
 * @param {boolean} props.isLoading - Loading state
 * @param {Function} props.onRowClick - Optional row click handler
 * @param {string} props.className - Additional CSS classes
 */
const CallsTable = ({
  calls = [],
  isLoading = false,
  onRowClick,
  className = '',
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedCallIndex, setSelectedCallIndex] = useState(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [playingAudio, setPlayingAudio] = useState(null);
  const [playingCallId, setPlayingCallId] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (playingAudio) {
        playingAudio.pause();
        playingAudio.currentTime = 0;
        playingAudio.removeAttribute('src');
        playingAudio.load();
      }
    };
  }, [playingAudio]);

  // Map our API data to match Figma design expectations
  const mapCallToFigmaFormat = (call) => ({
    id: call.call_id,
    time: formatDate(call.start_timestamp, 'MMM dd, h:mm a'), // Date and time like "Dec 13, 10:42 AM"
    callerId: call.from_number ? formatPhoneNumber(call.from_number) : '—',
    intent: getCallIntent(call), // Derive intent from call data
    duration: call.duration_ms ? formatDurationMMSS(call.duration_ms) : '0:00',
    agentType: "AI", // Since this is an AI calling system
    outcome: call.call_successful ? "Completed" : (call.call_status === 'ended' ? "Completed" : "Missed"),
    transcript: call.transcript || "",
    summary: call.summary || "Call completed",
    sentiment: call.user_sentiment || 0,
    originalCall: call // Keep reference to original call data
  });

  // Get call intent based on call data
  const getCallIntent = (call) => {
    // You can customize this logic based on your call data
    if (call.call_successful) return "New Order";
    if (call.disconnection_reason === 'user_hangup') return "Order Status";
    return "General";
  };

  // Filter calls based on search
  const filteredCalls = calls.map(mapCallToFigmaFormat).filter(call => {
    const matchesSearch = call.callerId.includes(searchQuery);
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredCalls.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCalls = filteredCalls.slice(startIndex, startIndex + itemsPerPage);

  // Handle row click
  const handleRowClick = (call) => {
    if (onRowClick) {
      onRowClick(call.originalCall);
    } else {
      // Find the index of the clicked call in the filtered list
      const index = filteredCalls.findIndex(c => c.id === call.id);
      setSelectedCall(call.originalCall);
      setSelectedCallIndex(index);
      setIsModalOpen(true);
    }
  };

  // Handle navigation to previous/next call
  const handleNavigate = (direction) => {
    if (selectedCallIndex === -1) return;

    let newIndex;
    if (direction === 'previous') {
      newIndex = selectedCallIndex - 1;
    } else {
      newIndex = selectedCallIndex + 1;
    }

    // Check bounds
    if (newIndex < 0 || newIndex >= filteredCalls.length) return;

    // Get the new call
    const newCall = filteredCalls[newIndex];
    setSelectedCall(newCall.originalCall);
    setSelectedCallIndex(newIndex);
  };

  // Check if navigation is possible
  const hasPrevious = selectedCallIndex > 0;
  const hasNext = selectedCallIndex >= 0 && selectedCallIndex < filteredCalls.length - 1;

  // Clean up an audio instance
  const cleanupAudio = (audio) => {
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  };

  // Handle recording playback
  const handlePlayRecording = (e, call) => {
    e.stopPropagation();
    setAudioError(null);
    const recordingUrl = call.originalCall?.recording_url || call.originalCall?.recording_multi_channel_url;

    if (!recordingUrl) return;

    // If this call is already playing, pause it
    if (playingCallId === call.id && playingAudio) {
      playingAudio.pause();
      setPlayingAudio(null);
      setPlayingCallId(null);
      return;
    }

    // Stop any currently playing audio
    cleanupAudio(playingAudio);

    // Create and play new audio
    const audio = new Audio(recordingUrl);

    const handleEnded = () => {
      setPlayingAudio(null);
      setPlayingCallId(null);
    };

    const handleError = () => {
      setPlayingAudio(null);
      setPlayingCallId(null);
      setAudioError('Unable to play recording.');
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    audio.play().then(() => {
      setPlayingAudio(audio);
      setPlayingCallId(call.id);
    }).catch(() => {
      setPlayingAudio(null);
      setPlayingCallId(null);
      setAudioError('Unable to play recording.');
    });
  };

  // Get outcome icon
  const getOutcomeIcon = (outcome) => {
    if (outcome === "Completed") {
      return <CheckCircle2 className="w-4 h-4" />;
    } else if (outcome === "Missed") {
      return <XCircle className="w-4 h-4" />;
    } else {
      return <Clock className="w-4 h-4" />;
    }
  };

  // Get outcome color
  const getOutcomeColor = (outcome) => {
    if (outcome === "Completed") return "text-[#3AD0E6]";
    if (outcome === "Missed") return "text-red-400";
    return "text-yellow-500";
  };

  // Loading state
  if (isLoading) {
    return (
      <Card className="bg-gray-800/50 border-gray-700 p-6">
        <div className="flex flex-col items-center justify-center text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AD0E6] mb-4"></div>
          <p className="text-gray-400">Loading calls...</p>
        </div>
      </Card>
    );
  }

  // Empty state
  if (!isLoading && calls.length === 0) {
    return (
      <Card className="bg-gray-800/50 border-gray-700 p-6">
        <div className="flex flex-col items-center justify-center text-center py-12">
          <XCircle className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            No calls found
          </h3>
          <p className="text-gray-400">
            Try adjusting your filters or date range
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      {audioError && (
        <div className="mb-4 bg-red-900/20 border border-red-700 rounded-lg p-3 flex items-center justify-between">
          <p className="text-sm text-red-400">{audioError}</p>
          <button onClick={() => setAudioError(null)} className="text-red-400 hover:text-red-300 ml-4">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}
      <Card className="bg-gray-800/50 border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h2 className="text-white text-xl">Call Logs</h2>
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by phone..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 bg-gray-700 border-gray-600 text-white w-64"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-gray-400 text-xs py-3 px-2">Date & Time</th>
                <th className="text-left text-gray-400 text-xs py-3 px-2">Caller ID</th>
                <th className="text-left text-gray-400 text-xs py-3 px-2">Duration</th>
                <th className="text-left text-gray-400 text-xs py-3 px-2">Outcome</th>
                <th className="text-left text-gray-400 text-xs py-3 px-2">Recording</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCalls.map((call) => (
                <tr 
                  key={call.id} 
                  className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(call)}
                >
                  <td className="text-gray-300 text-sm py-3 px-2">{call.time}</td>
                  <td className="text-gray-300 text-sm py-3 px-2">{call.callerId}</td>
                  <td className="text-gray-300 text-sm py-3 px-2">{call.duration}</td>
                  <td className="text-gray-300 text-sm py-3 px-2">
                    <span className={`flex items-center gap-1 ${getOutcomeColor(call.outcome)}`}>
                      {getOutcomeIcon(call.outcome)}
                      {call.outcome}
                    </span>
                  </td>
                  <td className="text-gray-300 text-sm py-3 px-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[#3AD0E6] hover:text-[#3AD0E6]/80 hover:bg-gray-700"
                      onClick={(e) => handlePlayRecording(e, call)}
                      disabled={!call.originalCall?.recording_url && !call.originalCall?.recording_multi_channel_url}
                      title={
                        call.originalCall?.recording_url || call.originalCall?.recording_multi_channel_url
                          ? playingCallId === call.id ? "Pause recording" : "Play recording"
                          : "No recording available"
                      }
                    >
                      {playingCallId === call.id ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-gray-400 text-sm">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredCalls.length)} of {filteredCalls.length} calls
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page 
                        ? "bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white" 
                        : "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                      }
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600 disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Call Detail Modal */}
      <CallDetailModal
        callId={selectedCall?.call_id}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCall(null);
          setSelectedCallIndex(-1);
        }}
        onNavigate={handleNavigate}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />
    </>
  );
};

export default CallsTable;

